/**
 * Safely extracts a string from any unknown value (primitive, nested { text, value, name, label, summary, tag_name, title, slug }, etc.)
 */
export function extractString(val: unknown, fallback = ''): string {
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed.startsWith('[object') && trimmed.endsWith(']')) return fallback;
    return val;
  }
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (val && typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    const candidateKeys = [
      'tag_name',
      'name',
      'label',
      'text',
      'value',
      'title',
      'tag',
      'summary',
      'slug',
      'description',
      'name_pt',
      'name_en',
    ];

    for (const key of candidateKeys) {
      if (key in obj && obj[key] !== undefined && obj[key] !== null) {
        const extracted = extractString(obj[key], '');
        if (extracted && !extracted.startsWith('[object')) return extracted;
      }
    }

    // If no known key matched, find the first string property that is not an id/pk/timestamp
    for (const [k, v] of Object.entries(obj)) {
      if (!['id', 'pk', 'uuid', 'created_at', 'updated_at', 'confidence'].includes(k)) {
        if (typeof v === 'string' && v.trim() && !v.startsWith('[object')) {
          return v;
        }
      }
    }
  }
  return fallback;
}

/**
 * Safely extracts a number from any unknown value
 */
export function extractNumber(val: unknown, fallback = 0): number {
  if (typeof val === 'number' && !isNaN(val)) return val;
  if (typeof val === 'string') {
    const parsed = parseFloat(val.replace(/,/g, ''));
    return isNaN(parsed) ? fallback : parsed;
  }
  if (val && typeof val === 'object') {
    if ('amount' in val) return extractNumber((val as { amount: unknown }).amount, fallback);
    if ('value' in val) return extractNumber((val as { value: unknown }).value, fallback);
    if ('text' in val) return extractNumber((val as { text: unknown }).text, fallback);
    if ('number' in val) return extractNumber((val as { number: unknown }).number, fallback);
  }
  return fallback;
}

/**
 * Safely extracts an array of strings from unknown list
 */
export function extractStringArray(val: unknown): string[] {
  if (!val || !Array.isArray(val)) return [];
  return val
    .map((item) => extractString(item))
    .filter((s) => Boolean(s) && !s.startsWith('[object'));
}
